import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Container,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../assets/icons/backArrow.svg";
import {
  CustomButton,
  CustomDialog,
  CustomInput,
  CustomTable,
} from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import CustomSelect from "../../global/components/CustomSelect/CustomSelect";
import strings from "../../global/constants/StringConstants";
import { handleErrorMessage, isTruthy } from "../../helpers/methods";
import history from "../../utils/history";
import { LightTooltip, theme } from "../../utils/styles";
import tasksStyles from "./Tasks.styles";
import { getTasksData, saveTasks, updateTasks } from "./TasksService";

export const formData = () => {
  return {
    name: {
      value: "",
      error: "",
    },
    status: {
      value: "to_do",
      error: "",
    },
    start_date: {
      value: "",
      error: "",
    },
    end_date: {
      value: "",
      error: "",
    },
    description: {
      value: "",
      error: "",
    },
    comment: {
      value: "",
      error: "",
    },
  } as any;
};

const Tasks = () => {
  const classes = tasksStyles;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [tasksList, setTasksList] = useState<any[]>([]);
  const [formFields, setFormFields] = useState(formData);

  const statusList = [
    { value: "to_do", content: "To Do" },
    { value: "in_progress", content: "In Progress" },
    { value: "on_hold", content: "On Hold" },
    { value: "testing", content: "Testing" },
    { value: "uat", content: "UAT" },
    { value: "completed", content: "Completed" },
  ];

  const tableHeader = [
    {
      name: `${t("name")}`,
      field: "displayName",
    },
    {
      name: `${t("status")}`,
      field: "displayStatus",
    },
    {
      name: `${t("startDate")}`,
      field: "displayStartDate",
    },
    {
      name: `${t("endDate")}`,
      field: "displayEndDate",
    },
    {
      name: "",
      field: "actions",
      align: "center",
    },
  ];

  useEffect(() => {
    getTasksList();
  }, []);

  useEffect(() => {
    if (tasksList.length > 0) {
      const filtered = tasksList.filter((task) => {
        const searchLower = searchQuery.toLowerCase();
        const nameMatch = task.name.toLowerCase().includes(searchLower);
        const descriptionMatch = task.description
          .toLowerCase()
          .includes(searchLower);
        const commentMatch = task.comment.toLowerCase().includes(searchLower);
        return nameMatch || descriptionMatch || commentMatch;
      });
      setFilteredList(filtered);
    } else {
      setFilteredList([]);
    }
  }, [searchQuery, tasksList]);

  const getTasksList = async () => {
    try {
      setIsLoading(true);
      const response = await getTasksData();
      const transformedData = response.tasks.map((task: any) => {
        return {
          id: task.id,
          name: task.name,
          displayName: (
            <LightTooltip
              placement="top"
              arrow
              sx={{
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#ffffff",
                  fontSize: "16px",
                  color: "#000000",
                },
              }}
              title={task.name}
            >
              <Typography sx={classes.tableLinkText}>{task.name}</Typography>
            </LightTooltip>
          ),
          displayStatus:
            statusList.find((s) => s.value === task.status)?.content ||
            task.status,
          displayStartDate: formatDateTime(task.start_date),
          displayEndDate: formatDateTime(task.end_date),
          start_date: task.start_date,
          end_date: task.end_date,
          comment: task.comment,
          status: task.status,
          description: task.description,
          actions: (
            <LightTooltip
              title={t("edit")}
              placement="top"
              sx={{
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#ffffff",
                  fontSize: "20px",
                  color: "#000000",
                },
              }}
            >
              <IconButton
                onClick={() => handleEdit(task)}
                sx={{
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </LightTooltip>
          ),
          searchableFields: {
            name: task.name.toLowerCase(),
            description: task.description.toLowerCase(),
            comment: task.comment.toLowerCase(),
          },
        };
      });
      setTasksList(transformedData);
      setFilteredList(transformedData);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  };

  const validateForm = () => {
    let isValid = true;
    const newFormFields = { ...formFields };

    // Validate name
    if (!isTruthy(formFields.name.value)) {
      newFormFields.name.error = "Name is required";
      isValid = false;
    }

    // Validate status
    if (!isTruthy(formFields.status.value)) {
      newFormFields.status.error = "Status is required";
      isValid = false;
    }

    // Validate start date
    if (!isTruthy(formFields.start_date.value)) {
      newFormFields.start_date.error = "Start date is required";
      isValid = false;
    }

    // Validate end date
    if (!isTruthy(formFields.end_date.value)) {
      newFormFields.end_date.error = "End date is required";
      isValid = false;
    }

    // Validate description
    if (!isTruthy(formFields.description.value)) {
      newFormFields.description.error = "Description is required";
      isValid = false;
    }

    setFormFields(newFormFields);
    return isValid;
  };

  const handleSaveTask = async () => {
    try {
      if (!validateForm()) return;

      setIsLoading(true);
      const payload = {
        name: formFields.name.value,
        status: formFields.status.value,
        start_date: formFields.start_date.value,
        end_date: formFields.end_date.value,
        description: formFields.description.value,
        comment: formFields.comment.value,
      };

      await saveTasks(payload);
      setOpen(false);
      setFormFields(formData());
      await getTasksList();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTask = async () => {
    try {
      if (!validateForm()) return;

      setIsLoading(true);
      const payload = {
        id: formFields.id,
        name: formFields.name.value,
        status: formFields.status.value,
        start_date: formFields.start_date.value,
        end_date: formFields.end_date.value,
        description: formFields.description.value,
        comment: formFields.comment.value,
      };

      await updateTasks(payload);
      setOpen(false);
      setFormFields(formData());
      await getTasksList();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Modify the handleEdit function
  const handleEdit = (task: any) => {
    // Format dates from API format to DD-MM-YYYY format for the form
    const formatDate = (dateString: string) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    setFormFields({
      id: task.id,
      name: { value: task.name, error: "" },
      status: { value: task.status, error: "" },
      start_date: {
        value: formatDate(task.start_date),
        error: "",
      },
      end_date: {
        value: formatDate(task.end_date),
        error: "",
      },
      description: { value: task.description, error: "" },
      comment: { value: task.comment, error: "" },
    });
    setOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredList.slice(startIndex, endIndex);
  };

  const handleFieldChange = (event: any) => {
    setFormFields({
      ...formFields,
      [event.target.name]: {
        ...formFields[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("tasks")}</Typography>
      </Box>
    );
  };

  const getActions = () => {
    return (
      <Stack direction="row" justifyContent="space-between" p={1}>
        <TextField
          size="small"
          placeholder={t("search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: "200px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255, 255, 255, 0.14)",
              backdropFilter: "blur(24px)",
              borderRadius: "8px",
              color: theme.palette.primary.contrastText,
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
            "& .MuiOutlinedInput-input": {
              "&::placeholder": {
                color: theme.palette.primary.contrastText,
                opacity: 0.7,
              },
            },
          }}
        />
        <CustomButton
          label={t("createTask")}
          type={strings.SECONDARY}
          onClick={() => setOpen(true)}
          customClasses={{ maxWidth: "300px", minHight: "46px" }}
        />
      </Stack>
    );
  };

  const getTable = () => {
    return (
      <Box p={1}>
        <Grid container>
          <Grid item sm={12} sx={{ overflowX: "auto", overflowY: "hidden" }}>
            <CustomTable
              rows={getCurrentPageData()}
              headers={tableHeader}
              rowsPerPage={rowsPerPage}
              paginationCount={filteredList.length}
              pageNumber={page + 1}
              handlePageChange={handleChangePage}
              handlePerPageData={handleChangeRowsPerPage}
              perPageData={rowsPerPage}
              isRowPerPageEnable={false}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const dialogHeaderContent = () => {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2} pt={5}>
        <Typography sx={classes.modalHeader} px={2}>
          {t("taskDetails")}
        </Typography>
        <Divider
          sx={{
            border: "1px solid #222222",
            width: "50%",
            height: "0px",
          }}
        />
      </Stack>
    );
  };

  const dialogBodyContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <CustomInput
            required
            name="name"
            id="name"
            label={t("name")}
            placeHolder="Enter task name"
            value={formFields.name.value}
            customClasses={classes.textField}
            customNameFieldClasses={classes.nameField}
            onChange={handleFieldChange}
            error={!isTruthy(formFields.name.value) && formFields.name.error}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomSelect
            required
            menuItems={statusList}
            onChange={handleFieldChange}
            id={"status"}
            name={"status"}
            label={t("status")}
            customClasses={classes.textField}
            customNameFieldClasses={classes.nameField}
            value={formFields.status.value}
            error={formFields.status.error}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box mb={1}>
              <InputLabel required sx={classes.nameField}>
                {t("startDate")}
              </InputLabel>
            </Box>
            <DatePicker
              value={
                formFields.start_date.value
                  ? dayjs(formFields.start_date.value, "DD-MM-YYYY")
                  : null
              }
              onChange={(newValue: any) => {
                const formattedDate = newValue.format("DD-MM-YYYY");
                setFormFields({
                  ...formFields,
                  start_date: {
                    value: formattedDate,
                    error: "",
                  },
                });
              }}
              sx={classes.textField}
            />
            {formFields.start_date.error && (
              <FormHelperText error sx={classes.errorStyle}>
                {formFields.start_date.error}
              </FormHelperText>
            )}
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box mb={1}>
              <InputLabel required sx={classes.nameField}>
                {t("endDate")}
              </InputLabel>
            </Box>
            <DatePicker
              value={
                formFields.end_date.value
                  ? dayjs(formFields.end_date.value, "DD-MM-YYYY")
                  : null
              }
              onChange={(newValue: any) => {
                const formattedDate = newValue.format("DD-MM-YYYY");
                setFormFields({
                  ...formFields,
                  end_date: {
                    value: formattedDate,
                    error: "",
                  },
                });
              }}
              sx={classes.textField}
            />
            {formFields.end_date.error && (
              <FormHelperText error sx={classes.errorStyle}>
                {formFields.end_date.error}
              </FormHelperText>
            )}
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <CustomInput
            required
            label={t("description")}
            name="description"
            id="description"
            placeHolder="Enter task description"
            multiline
            rows={6}
            value={formFields.description.value}
            customNameFieldClasses={classes.nameField}
            customClasses={classes.textField}
            onChange={handleFieldChange}
            error={
              !isTruthy(formFields.description.value) &&
              formFields.description.error
            }
          />
        </Grid>
        <Grid item xs={12}>
          <CustomInput
            name="comment"
            id="comment"
            label={t("comment")}
            placeHolder="Enter task comment"
            value={formFields.comment.value}
            customNameFieldClasses={classes.nameField}
            customClasses={classes.textField}
            onChange={handleFieldChange}
            error={
              !isTruthy(formFields.comment.value) && formFields.comment.error
            }
          />
        </Grid>
        <Grid item xs={12} textAlign={"center"}>
          <CustomButton
            label={formFields.id ? t("update") : t("save")}
            type={strings.PRIMARY}
            onClick={formFields.id ? handleUpdateTask : handleSaveTask}
            customClasses={{ maxWidth: "300px" }}
          />
        </Grid>
      </Grid>
    );
  };

  const getTaskDialog = () => {
    return (
      <CustomDialog
        width="1000px"
        borderRadius="10px"
        isDialogOpen={open}
        closable={true}
        closeButtonVisibility={true}
        handleDialogClose={() => {
          setOpen(false);
          setFormFields(formData());
        }}
        dialogHeaderContent={dialogHeaderContent()}
        dialogBodyContent={dialogBodyContent()}
      />
    );
  };

  const getTasks = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Container maxWidth="xl" sx={{ px: 1 }}>
                  {getHeader()}
                  {getActions()}
                  {getTable()}
                </Container>
              </Box>
            </Box>
          </Box>
        </Box>
        {getTaskDialog()}
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };
  return getTasks();
};

export default Tasks;
